import "../external/lightbox";
import { HtmlRenderer, Parser } from "commonmark";
import QRCode from "qrcode";
import { C } from "./component";
import { ConfirmationDialogHelper } from "./confirmation-dialog-helper";
import { DialogTable } from "./dialog-table";
import { Form, FormDisplayer } from "../forms/form";
import { Helper } from "./helper";
import { Icon, IconHelper } from "./icon-helper";
import { Requester } from "./requester";
import { MessageDisplayer } from "./message-displayer";
import { DownloadHelper } from "./download-helper";
import { SeatingPlan } from "./seating-plan";
import { MapCell } from "../maps/map-cell";
import { NavigationUrlHelper } from "../navigation/navigation-url-helper";
import { Disposable, PageNavigator } from "../navigation/page-navigator";
import { Application } from "../navigation/application";
import { ModalBuilder } from "./modal-builder";
import { ItemPage } from "../dashboard/item-page";
import { ButtonBuilder } from "./button-builder";
import { Dom } from "./dom";

// ReSharper disable InconsistentNaming
export interface DisplayValue {
    Type: string;
    Text: string;
    Url: string;
    BackgroundColor: string;
    Description: string;
    Image: string;
    LeftIcon: IconInfo;
    Icons: IconInfo[];
    OnChangeType: string;
}

export interface IconInfo {
    Icon: string;
    Description: string;
    Color: string;
}

export interface LocationDisplayValue extends DisplayValue {
    StartLocation;
    EndLocation;
    ProjectLocation;
}

// ReSharper restore InconsistentNaming

class DisposeSecondary implements Disposable
{
    dispose(): void
    {
        Application.secondaryElement.empty();
    }

    enable(): void
    {
    }

    disable(): void
    {
    }
}

export class Displayer
{
    public static secondaryDisposer = new DisposeSecondary();
    private static imageGroupCounter = 0;

    static setValue(cell,
                    displayValue: DisplayValue,
                    onDataChanged?: (onChangeType: string) => void,
                    disableLinks: boolean = false,
                    forceNoWrap: boolean = false)
    {
        cell.empty();
        cell.unbind("click");
        cell.css("background-color", "");
        if (!displayValue)
        {
            return;
        }

        if (displayValue.Type === "Markdown")
        {
            if (Helper.hasValue(displayValue.Text))
            {
                const markdownReader = new Parser();
                const markdownWriter = new HtmlRenderer();
                cell.html(markdownWriter.render(markdownReader.parse(Helper.removeScriptTags(displayValue.Text))));
            }
            return;
        }

        if (displayValue.BackgroundColor)
        {
            cell.css("background-color", displayValue.BackgroundColor);
        }
        const imageGroupCounter = this.imageGroupCounter++;
        this.addDisplayElement(cell, displayValue, onDataChanged, disableLinks, forceNoWrap, imageGroupCounter);
    }

    private static addDisplayElement(cell,
                                     displayValue: DisplayValue,
                                     onDataChanged: (onChangeType: string) => void,
                                     disableLinks: boolean,
                                     forceNoWrap: boolean,
                                     imageGroupCounter: number)
    {
        if (displayValue.Type === "Array")
        {
            let isFirst = true;
            for (const child of (<any>displayValue).Values)
            {
                if (isFirst)
                {
                    isFirst = false;
                }
                else
                {
                    C.create((<any>displayValue).Separator).appendTo(cell);
                }
                this.addDisplayElement(cell, child, onDataChanged, disableLinks, forceNoWrap, imageGroupCounter);
            }
        }
        else
        {
            cell.append(this.getDisplayElement(displayValue, onDataChanged, disableLinks, forceNoWrap, imageGroupCounter));
        }
    }

    private static getDisplayElement(displayValue: DisplayValue,
                                     onDataChanged: (onChangeType: string) => void,
                                     disableLinks: boolean,
                                     forceNoWrap: boolean,
                                     imageGroupCounter: number): HTMLElement
    {
        const hasLinks = !disableLinks && displayValue.Type !== "Text";
        const element = hasLinks ? Dom.create("a") : Dom.createSpan();
        if (displayValue.Text)
        {
            const text = (displayValue.Image ? " " : "") + displayValue.Text + (displayValue.Icons.length === 0 ? "" : " ");
            if (!hasLinks)
            {
                if (forceNoWrap)
                {
                    element.style.whiteSpace = "nowrap";
                }
                else
                {
                    // pre-wrap is needed to preserve line breaks in text
                    element.style.whiteSpace = "pre-wrap";
                }
                if (displayValue.Type === "Text")
                {
                    element.innerHTML = Helper.urlify(Helper.escapeHtml(text));
                }
                else
                {
                    element.innerText = text;
                }
                if (displayValue.Description)
                {
                    element.setAttribute("data-container", "body");
                    element.setAttribute("title", displayValue.Description);
                    C.create(element).tooltip();
                }
            }
            else
            {
                element.innerText = text;
                element.style.cursor = "pointer";
            }
        }
        if (displayValue.Image)
        {
            const img = Dom.create("img", "img-circle", "img-responsive");
            img.setAttribute("src", displayValue.Image);
            img.style.display = "inline-block";
            element.prepend(img);
        }
        if (displayValue.LeftIcon)
        {
            element.prepend(this.getIconElement(displayValue.LeftIcon));
        }
        for (const iconInfo of displayValue.Icons)
        {
            element.append(this.getIconElement(iconInfo));
        }
        if (hasLinks)
        {
            if (displayValue.Type === "Link")
            {
                element.setAttribute("href", displayValue.Url);
                element.setAttribute("target", "_blank");
            }
            else if (displayValue.Type === "File")
            {
                const url = DownloadHelper.getFileDownloadUrl(displayValue.Url);
                const hasImagePreview = Helper.isImage(displayValue.Text);
                const hasPdfPreview = (displayValue.Text || "").toLowerCase().endsWith(".pdf") && !Helper.isMobile();
                if (!hasImagePreview && !hasPdfPreview)
                {
                    element.setAttribute("href", url);
                }
                else
                {
                    const linkContainer = Dom.createSpan();
                    linkContainer.append(element);
                    Dom.addSpan(linkContainer).innerText = " ";

                    const downloadLink = Dom.add("a", linkContainer);
                    downloadLink.setAttribute("href", url);
                    const downloadIcon = Dom.addSpan(downloadLink, "icon-cell");
                    IconHelper.addIcon(C.create(downloadIcon), Icon.download);
                    downloadIcon.style.cursor = "pointer";

                    if (hasImagePreview)
                    {
                        element.setAttribute("href", url);
                        element.setAttribute("data-lightbox", "group-" + imageGroupCounter);
                    }
                    else if (hasPdfPreview)
                    {
                        element.addEventListener("click", async () =>
                        {
                            this.openPdfPreview(displayValue, () =>
                            {
                                if (onDataChanged)
                                {
                                    onDataChanged(displayValue.OnChangeType);
                                }
                            });
                        });
                    }
                    return linkContainer;
                }
            }
            else
            {
                element.addEventListener("click", async () => await this.DoClickAction(displayValue, onDataChanged, imageGroupCounter));
            }
        }
        return element;
    }

    private static getIconElement(iconInfo: IconInfo): HTMLElement
    {
        const element = C.span.addClass("icon-cell").attr("title", iconInfo.Description).tooltip();
        if (iconInfo.Color)
        {
            IconHelper.addColorfulIcon(element, Icon[iconInfo.Icon], iconInfo.Color);
        }
        else
        {
            IconHelper.addIcon(element, Icon[iconInfo.Icon]);
        }
        return element[0];
    }

    private static async DoClickAction(displayValue: DisplayValue,
                                       onDataChanged: (onChangeType: string) => void,
                                       imageGroupCounter: number)
    {
        const displayValueAny = <any>displayValue;
        if (displayValue.Type === "Form")
        {
            const form = new Form(displayValue.Url);
            if (onDataChanged)
            {
                form.onDataChanged = value =>
                {
                    onDataChanged(displayValue.OnChangeType ?? (value === "MultipleItemsEdited" ? "FullRefresh" : null));
                };
            }
            await FormDisplayer.openInModal(form);
        }
        else if (displayValue.Type === "Confirmation")
        {
            ConfirmationDialogHelper.showDialog(displayValueAny.ConfirmationText,
                displayValueAny.ConfirmedUrl, () =>
                {
                    if (onDataChanged)
                    {
                        onDataChanged(displayValue.OnChangeType);
                    }
                });
        }
        else if (displayValue.Type === "DialogTable")
        {
            await new DialogTable(displayValue.Url, () => onDataChanged(null)).open();
        }
        else if (displayValue.Type === "Location")
        {
            new MapCell().showLocation(<LocationDisplayValue>displayValue);
        }
        else if (displayValue.Type === "ItemPage")
        {
            PageNavigator.hideMainElement();
            Application.secondaryElement.empty();
            PageNavigator.addDisposableElement(this.secondaryDisposer);
            const currentPage = NavigationUrlHelper.getCurrentPage();
            currentPage.itemPage = displayValue.Url;
            NavigationUrlHelper.setCurrentPage(currentPage);
            await new ItemPage(displayValue.Url, () =>
            {
                Application.secondaryElement.empty();
                PageNavigator.showMainElement();
            }).appendTo(Application.secondaryElement);
        }
        else if (displayValue.Type === "SeatingPlanButton")
        {
            await new SeatingPlan(displayValue.Url).load(async (seats) =>
            {
                try
                {
                    await Requester.send(Requester.getUrl(displayValueAny.SaveUrl, { seat: seats[0] }));
                    return true;
                }
                catch (err)
                {
                    MessageDisplayer.showErrorFromServer(err);
                    return false;
                }
            });
        }
        else if (displayValue.Type === "QrCode")
        {
            const qrData = await QRCode.toDataURL(displayValue.Url);
            const link = C.a.attr("href", qrData).attr("data-lightbox", "group-" + imageGroupCounter).appendTo(C.body);
            link[0].click();
            link.remove();
        }
        else if (displayValue.Type === "Copy")
        {
            await navigator.clipboard.writeText(displayValue.Url);
        }
        return false;
    }

    public static openPdfPreview(displayValue: DisplayValue, onChange: () => void)
    {
        const modal = ModalBuilder.createModal(displayValue.Text);
        modal.dialog.css("width", "880px").css("height", "1400px");
        C.object.addClass("preview-pdf").attr("data", DownloadHelper.getFileDownloadUrl(displayValue.Url, { pdfPreview: "true" })).appendTo(modal.body);
        if ((<any>displayValue).ButtonText)
        {
            ButtonBuilder.get({
                name: (<any>displayValue).ButtonText,
                onClick: async () => ConfirmationDialogHelper.showDialog(
                    (<any>displayValue).ConfirmationText,
                    (<any>displayValue).ConfirmedUrl,
                    () =>
                    {
                        ModalBuilder.removeModal(modal.container);
                        onChange();
                    }
                )
            }).appendTo(modal.body);
        }
        modal.container.modal({ show: true });
    }
}
